import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useLottery } from "@/hooks/use-lottery-selection";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";
import { Flame, Snowflake, Clock } from "lucide-react";

type StatRow = {
  number: number;
  frequency: number;
  last_contest: number | null;
  delay: number | null;
  total_draws: number;
};

type Period = "20" | "100" | "500" | "all";

const Frequencia = () => {
  const { selected, config } = useLottery();
  const [period, setPeriod] = useState<Period>("all");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["frequency-number-stats", selected, period],
    queryFn: async () => {
      const lastN = period === "all" ? null : Number(period);
      const { data, error } = await supabase.rpc("get_number_stats", {
        _lottery_slug: selected,
        _last_n: lastN,
      });
      if (error) throw error;
      return (data ?? []) as StatRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const stats = Array.isArray(data) ? data : [];
  const totalDraws = stats[0]?.total_draws ?? 0;

  const sortedByFreq = useMemo(() => [...stats].sort((a, b) => b.frequency - a.frequency), [stats]);

  const maxFreq = stats.reduce((m, s) => Math.max(m, s.frequency), 0) || 1;
  const minFreq = stats.reduce((m, s) => Math.min(m, s.frequency), Infinity) || 0;

  const heatColor = (freq: number) => {
    if (maxFreq === minFreq) return "hsl(var(--muted))";
    const t = (freq - minFreq) / (maxFreq - minFreq);
    // do azul-frio (baixa) ao dourado-quente (alta)
    const hue = 200 - t * 160; // 200 (azul) -> 40 (dourado)
    const sat = 60 + t * 25;
    const light = 35 + (1 - t) * 15;
    return `hsl(${hue}, ${sat}%, ${light}%)`;
  };

  // Heatmap de atrasos: quanto maior o atraso, mais "frio" (a dezena está fria/atrasada).
  // Invertemos a escala: maior atraso = vermelho/quente visual? O usuário pediu atrasos:
  // dezenas com maior atraso devem se destacar. Usamos gradiente verde (recém-sorteadas) -> vermelho (muito atrasadas).
  const delays = stats.map((s) => s.delay ?? 0);
  const maxDelay = delays.reduce((m, d) => Math.max(m, d), 0) || 1;
  const minDelay = delays.reduce((m, d) => Math.min(m, d), Infinity) || 0;
  const delayColor = (delay: number) => {
    if (maxDelay === minDelay) return "hsl(var(--muted))";
    const t = (delay - minDelay) / (maxDelay - minDelay);
    // verde (acabou de sair) -> amarelo -> vermelho (muito atrasada)
    const hue = 140 - t * 140; // 140 (verde) -> 0 (vermelho)
    const sat = 55 + t * 30;
    const light = 38 + (1 - t) * 12;
    return `hsl(${hue}, ${sat}%, ${light}%)`;
  };

  const topByFreq = sortedByFreq.slice(0, 25);
  const delayByNumber = new Map(stats.map((s) => [s.number, s.delay ?? 0]));
  const topBarData = topByFreq.map((s) => ({
    number: s.number,
    frequency: s.frequency,
    delay: delayByNumber.get(s.number) ?? 0,
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-serif text-3xl text-foreground">Frequência e Atraso — {config.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {totalDraws > 0
              ? `Análise sobre ${totalDraws.toLocaleString("pt-BR")} concursos.`
              : "Carregando estatísticas..."}
          </p>
        </div>
        <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
          <TabsList className="bg-card border border-lt-border">
            <TabsTrigger value="20">Últimos 20</TabsTrigger>
            <TabsTrigger value="100">Últimos 100</TabsTrigger>
            <TabsTrigger value="500">Últimos 500</TabsTrigger>
            <TabsTrigger value="all">Histórico completo</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-64 w-full" />
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-72 w-full" />
            <Skeleton className="h-72 w-full" />
          </div>
        </div>
      ) : isError || stats.length === 0 ? (
        <Card className="border-lt-border bg-card">
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            Não foi possível carregar as estatísticas desta loteria agora. Suas dezenas escolhidas em Combinações e Bolões continuam salvas.
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Heatmap das dezenas */}
          <Card className="border-lt-border bg-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Mapa de calor — frequência das dezenas</CardTitle>
              <p className="text-xs text-muted-foreground">
                Dezenas mais quentes (douradas) saíram com mais frequência. Mais frias (azuis) saíram menos.
              </p>
            </CardHeader>
            <CardContent>
              <div
                className="grid gap-1.5"
                style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 20)}, minmax(0, 1fr))` }}
              >
                {stats.map((s) => (
                  <div
                    key={s.number}
                    className="aspect-square rounded-md flex flex-col items-center justify-center text-foreground transition-transform hover:scale-110 antialiased"
                    style={{
                      backgroundColor: heatColor(s.frequency),
                      textShadow: "0 1px 2px rgba(0,0,0,0.45)",
                    }}
                    title={`${String(s.number).padStart(2, "0")} — ${s.frequency} vezes • atraso ${s.delay ?? "-"} concursos`}
                  >
                    <span className="text-[13px] font-bold leading-none tabular-nums">
                      {String(s.number).padStart(2, "0")}
                    </span>
                    <span className="mt-0.5 text-[10px] font-medium leading-none tabular-nums opacity-90">
                      {s.frequency}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5"><Snowflake className="h-3 w-3" /> Mais fria: {minFreq}x</span>
                <div className="h-2 flex-1 mx-4 rounded-full bg-gradient-to-r from-[hsl(200,60%,35%)] via-[hsl(120,70%,40%)] to-[hsl(40,85%,50%)]" />
                <span className="flex items-center gap-1.5"><Flame className="h-3 w-3" /> Mais quente: {maxFreq}x</span>
              </div>
            </CardContent>
          </Card>

          {/* Heatmap dos atrasos */}
          <Card className="border-lt-border bg-card">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Mapa de calor — atraso das dezenas</CardTitle>
              <p className="text-xs text-muted-foreground">
                Dezenas em vermelho estão há mais concursos sem sair. Em verde, saíram recentemente.
              </p>
            </CardHeader>
            <CardContent>
              <div
                className="grid gap-1.5"
                style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 20)}, minmax(0, 1fr))` }}
              >
                {stats.map((s) => (
                  <div
                    key={s.number}
                    className="aspect-square rounded-md flex flex-col items-center justify-center text-foreground transition-transform hover:scale-110 antialiased"
                    style={{
                      backgroundColor: delayColor(s.delay ?? 0),
                      textShadow: "0 1px 2px rgba(0,0,0,0.45)",
                    }}
                    title={`${String(s.number).padStart(2, "0")} — atraso ${s.delay ?? "-"} concursos`}
                  >
                    <span className="text-[13px] font-bold leading-none tabular-nums">
                      {String(s.number).padStart(2, "0")}
                    </span>
                    <span className="mt-0.5 text-[10px] font-medium leading-none tabular-nums opacity-90">
                      {s.delay ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" /> Recente: {minDelay}</span>
                <div className="h-2 flex-1 mx-4 rounded-full bg-gradient-to-r from-[hsl(140,55%,38%)] via-[hsl(60,75%,45%)] to-[hsl(0,85%,50%)]" />
                <span className="flex items-center gap-1.5"><Flame className="h-3 w-3" /> Atrasada: {maxDelay}</span>
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de barras */}
          <Card className="border-lt-border bg-card">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Flame className="h-4 w-4 text-primary" /> Top 25 mais sorteadas
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Ordenadas da mais para a menos frequente. O número <strong>acima</strong> da barra é a frequência (quantas vezes saiu); o número <strong>dentro da base</strong> da barra é o atraso atual (concursos sem sair).
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={420}>
                  <BarChart data={topBarData} margin={{ top: 28, right: 16, bottom: 8, left: -10 }}>
                    <XAxis
                      dataKey="number"
                      tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                      tickFormatter={(v) => String(v).padStart(2, "0")}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 6,
                        fontSize: 12,
                        color: "hsl(var(--foreground))",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--foreground))", fontWeight: 500 }}
                      cursor={{ fill: "hsl(var(--foreground) / 0.08)" }}
                      formatter={(value: number, name: string) =>
                        name === "delay"
                          ? [`${value} concursos`, "Atraso"]
                          : [`${value} vezes`, "Frequência"]
                      }
                      labelFormatter={(v) => `Dezena ${String(v).padStart(2, "0")}`}
                    />
                    <Bar dataKey="frequency" radius={[4, 4, 0, 0]}>
                      {topBarData.map((s) => (
                        <Cell key={s.number} fill={heatColor(s.frequency)} />
                      ))}
                      <LabelList
                        dataKey="frequency"
                        position="top"
                        style={{ fill: "hsl(var(--foreground))", fontSize: 11, fontWeight: 600 }}
                      />
                      <LabelList
                        dataKey="delay"
                        position="insideBottom"
                        offset={6}
                        style={{ fill: "hsl(var(--primary-foreground))", fontSize: 11, fontWeight: 600 }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default Frequencia;
